import { useAlerts } from '@/composables/alert/useAlerts'
import { alertEnum } from '@/enums/AlertEnum'
import type { FetchApiParams, FetchApiWithStatus } from '@/types/FetchApi'
import type { AlertItem } from '@/types/Alert'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
import { isNotBlank } from '@/utils/String'

const { addAlert } = useAlerts()

export let isReloadingSession = false

export function resetSessionReloadFlag() {
  isReloadingSession = false
}

export type ErrorFormatter = (messages: string[]) => AlertItem[] | string

const defaultErrorFormatter: ErrorFormatter = (messages: string[]) => {
  return messages.map((msg, index) => `${String(index + 1)}. ${msg}`).join('\n')
}

export interface FetchApiErrorOptions {
  formatter?: ErrorFormatter
  title?: string
  message?: string
  suppressAlert?: boolean
}

export class FetchApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly messages?: string[],
  ) {
    super(message)
    this.name = 'FetchApiError'
  }
}

// Function overloads for type-safe return types
async function fetchApi<T>(
  params: FetchApiParams & { returnStatus: true },
  errorOptions?: FetchApiErrorOptions,
): Promise<FetchApiWithStatus<T>>
async function fetchApi<T>(
  params: FetchApiParams & { returnStatus?: false },
  errorOptions?: FetchApiErrorOptions,
): Promise<T>

// Implementation
async function fetchApi<T>(
  params: FetchApiParams,
  errorOptions?: FetchApiErrorOptions,
): Promise<T | FetchApiWithStatus<T>> {
  const {
    endpoint,
    method,
    body,
    query,
    responseType = 'json',
    timeout = 20000,
    returnStatus = false,
  } = params

  // ────────────────────────────
  // Base URL (tests = http://localhost)
  // ────────────────────────────
  const origin =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : 'http://localhost'

  const headers: Record<string, string> = {
    Accept: 'application/json',
  }

  if (body) {
    headers['Content-Type'] = 'application/json'
  }

  // ────────────────────────────
  // Setup abort controller for timeout (if timeout is not null)
  // ────────────────────────────
  let abortController: AbortController | undefined
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  if (timeout !== null) {
    abortController = new AbortController()
    timeoutId = setTimeout(() => abortController?.abort(), timeout)
  }

  const init: RequestInit = {
    method: method ?? 'GET',
    credentials: 'include',
    referrer: '',
    headers,
    ...(abortController ? { signal: abortController.signal } : {}),
  }

  if (body) {
    init.body = JSON.stringify(body)
  }

  const urlObj = new URL(endpoint, origin)

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      urlObj.searchParams.append(key, String(value))
    })
  }

  interface ParsedApiResponse {
    message?: string | string[]
    errors?: Record<string, unknown>
    [key: string]: unknown
  }

  const safeParse = async (response: Response): Promise<ParsedApiResponse | string> => {
    const text = await response.text()

    if (!text.trim()) return {}

    try {
      return JSON.parse(text) as ParsedApiResponse
    } catch {
      return text
    }
  }

  try {
    const response = await fetch(urlObj.toString(), init)
    const status = response.status

    const { isPreProduction } = useFeatureFlags()
    if (isPreProduction) {
      void import('@sentry/vue').then((Sentry) => {
        const method = init.method ?? 'GET'
        Sentry.addBreadcrumb({
          category: 'api',
          message: `${method} ${endpoint}`,
          data: { status, endpoint },
          level: response.ok ? 'info' : 'warning',
        })
      })
    }

    // Clear timeout on successful response (if timeout was set)
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }

    // Handle blob response type
    if (responseType === 'blob') {
      if (!response.ok) {
        // Try to parse error as JSON
        const parsed = await safeParse(response)
        if (typeof parsed === 'object' && parsed.message) {
          const msg =
            typeof parsed.message === 'string' ? parsed.message : parsed.message.join(', ')
          addAlert(msg, alertEnum.OnlyMessage)
          throw new Error(msg)
        }
        addAlert('error_occurred_try_again', alertEnum.Danger)
        throw new Error('Failed to fetch blob')
      }
      const blobData = (await response.blob()) as T
      return returnStatus ? { status, data: blobData } : blobData
    }

    const parsed = await safeParse(response)

    // ────────────────────────────
    // SESSION EXPIRED → reload to trigger router guard
    // ────────────────────────────
    if (status === 401 && !isReloadingSession) {
      isReloadingSession = true
      window.location.reload()
      throw new Error('Session expired')
    }

    // ────────────────────────────
    // ERROR STATUS → handle alerts / throws
    // ────────────────────────────
    if (!response.ok) {
      const suppress = errorOptions?.suppressAlert === true

      if (typeof parsed === 'object' && parsed.errors) {
        const messages: string[] = []

        Object.values(parsed.errors).forEach((value) => {
          if (Array.isArray(value)) {
            value.forEach((msg) => typeof msg === 'string' && messages.push(msg))
          }
        })

        if (!suppress)
          messages.forEach((m) => {
            addAlert(m, alertEnum.Danger)
          })
        throw new FetchApiError('Validation error', status)
      }

      if (typeof parsed === 'object' && parsed.message) {
        if (Array.isArray(parsed.message)) {
          const formatter = errorOptions?.formatter ?? defaultErrorFormatter
          const formatted = formatter(parsed.message)

          if (!suppress) {
            if (typeof formatted === 'string') {
              // Simple string message
              addAlert(formatted, alertEnum.Danger)
            } else {
              // AlertItem[] - use with title and items
              const message = isNotBlank(errorOptions?.message)
                ? errorOptions.message
                : 'Validation error'
              addAlert(message, alertEnum.Danger, {
                title: errorOptions?.title,
                items: formatted,
              })
            }
          }
          throw new FetchApiError('Validation error', status, parsed.message)
        } else {
          if (!suppress) addAlert(parsed.message, alertEnum.OnlyMessage)
          throw new FetchApiError(parsed.message, status)
        }
      }

      if (!suppress) addAlert('error_occurred_try_again', alertEnum.Danger)
      throw new FetchApiError('something_went_wrong', status)
    }

    if (typeof parsed === 'object' && parsed.message) {
      const msg = typeof parsed.message === 'string' ? parsed.message : parsed.message.join(', ')
      addAlert(msg, alertEnum.Success)
    }

    const data = parsed as T
    return returnStatus ? { status, data } : data
  } catch (err) {
    // Clear timeout on error (if timeout was set)
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }

    // Handle timeout errors silently (no alert)
    // DOMException might not be instance of Error, so check name property directly
    if ((err as { name?: string }).name === 'AbortError') {
      throw new Error('Request timeout')
    }

    console.error('fetch error:', err)
    throw err
  }
}

export default fetchApi
